import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CompanyDetailDto, CompanyUpsertInput } from "@hisense/shared";
import { useCompanyDetails, useSaveCompany } from "@/api/hooks";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { ApiClientError, getLocalizedError, uploadImage } from "@/lib/api";
import { IMAGE_ACCEPT } from "@/lib/uploadAccept";
import { ConfigurationHistory } from "./ConfigurationHistory";
const toDraft = (c?: CompanyDetailDto): CompanyUpsertInput => ({
  beneficiary: c?.beneficiary ?? "",
  beneficiaryBank: c?.beneficiaryBank ?? "",
  bankAddress: c?.bankAddress ?? "",
  accountNumberHkd: c?.accountNumberHkd ?? "",
  accountNumberUsd: c?.accountNumberUsd ?? "",
  accountNumberRmb: c?.accountNumberRmb ?? "",
  swiftCode: c?.swiftCode ?? "",
  logoUrl: c?.logoUrl ?? "",
  logoKey: c?.logoKey ?? "",
  isDefault: c?.isDefault ?? false,
  active: c?.active ?? true,
  expectedRevision: c?.revision,
});
export function PaymentCompanies({
  canEdit,
  onDirtyChange,
}: {
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const query = useCompanyDetails(canEdit);
  const save = useSaveCompany();
  const [editing, setEditing] = useState<CompanyDetailDto | "new" | null>(null);
  const [draft, setDraft] = useState<CompanyUpsertInput>(toDraft());
  const [initial, setInitial] = useState<CompanyUpsertInput>(toDraft());
  const [showInactive, setShowInactive] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [discard, setDiscard] = useState(false);
  const dirty = !!editing && JSON.stringify(draft) !== JSON.stringify(initial);
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);
  if (!canEdit) return null;
  if (query.isLoading) return <LoadingState />;
  if (query.error)
    return (
      <ErrorState
        message={t("settings.errorLoad")}
        onRetry={() => query.refetch()}
      />
    );
  const open = (company?: CompanyDetailDto) => {
    const d = toDraft(company);
    setEditing(company ?? "new");
    setInitial(d);
    setDraft(d);
    setError("");
    setReviewed(false);
    setDiscard(false);
  };
  const close = () => {
    if (save.isPending || uploading) return;
    if (dirty) setDiscard(true);
    else setEditing(null);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewed || uploading || save.isPending) return;
    setError("");
    try {
      await save.mutateAsync({
        id: editing === "new" ? undefined : editing?.id,
        body: draft,
      });
      setEditing(null);
    } catch (err) {
      setError(getLocalizedError(err, t, "settings.errorSave"));
      if (err instanceof ApiClientError && err.code === "company_changed")
        void query.refetch();
    }
  };
  const field = (
    key:
      | "beneficiary"
      | "beneficiaryBank"
      | "bankAddress"
      | "swiftCode"
      | "accountNumberUsd"
      | "accountNumberHkd"
      | "accountNumberRmb",
    label: string,
  ) => (
    <div key={key}>
      <FieldLabel htmlFor={`co-${key}`}>{label}</FieldLabel>
      <Input
        id={`co-${key}`}
        value={draft[key] ?? ""}
        required={key === "beneficiary"}
        maxLength={
          key === "bankAddress"
            ? 500
            : key === "swiftCode"
              ? 40
              : key.startsWith("accountNumber")
                ? 80
                : 200
        }
        disabled={save.isPending}
        onChange={(e) => {
          setDraft({ ...draft, [key]: e.target.value });
          setReviewed(false);
        }}
      />
    </div>
  );
  return (
    <section className="rounded-md border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">
            {t("settings.paymentCompanies")}
          </h3>
          <p className="mt-1 text-xs text-ink-soft">
            {t("settingsWorkspace.bankImpact")}
          </p>
        </div>
        <Button size="sm" onClick={() => open()}>
          {t("settings.addCompany")}
        </Button>
      </div>
      <label className="my-3 flex items-center gap-2 text-xs">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
        />
        {t("settingsWorkspace.includeInactive")}
      </label>
      <div className="space-y-2">
        {query.data
          ?.filter((c) => showInactive || c.active)
          .map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between gap-4 rounded-sm border border-line p-3"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">{c.beneficiary}</p>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-ink-soft">
                  {c.isDefault && (
                    <span className="text-brand">
                      {t("settings.defaultCompany")}
                    </span>
                  )}
                  {!c.active && (
                    <span className="text-warn">
                      {t("settingsWorkspace.inactive")}
                    </span>
                  )}
                  <span>
                    {c.beneficiaryBank || t("settingsWorkspace.bankIncomplete")}
                  </span>
                </div>
                <p className="mt-1 font-mono text-xs text-ink-faint">
                  {[
                    c.accountNumberUsd && `USD ${c.accountNumberUsd}`,
                    c.accountNumberHkd && `HKD ${c.accountNumberHkd}`,
                    c.accountNumberRmb && `RMB ${c.accountNumberRmb}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label={t("settingsWorkspace.editCompany", {
                  name: c.beneficiary,
                })}
                onClick={() => open(c)}
              >
                {t("common.edit")}
              </Button>
            </div>
          ))}
      </div>
      {editing && (
        <Modal
          title={
            editing === "new"
              ? t("settings.addCompany")
              : t("settingsWorkspace.editCompany", {
                  name: editing.beneficiary,
                })
          }
          closeLabel={t("common.cancel")}
          onClose={close}
          className="max-w-3xl"
        >
          <div className="min-h-0 overflow-y-auto p-4">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {field("beneficiary", t("settings.beneficiary"))}
                {field("beneficiaryBank", t("settings.beneficiaryBank"))}
                {field("bankAddress", t("settings.bankAddress"))}
                {field("swiftCode", t("settings.swiftCode"))}
                {field("accountNumberUsd", t("settings.accountNumberUsd"))}
                {field("accountNumberHkd", t("settings.accountNumberHkd"))}
                {field("accountNumberRmb", t("settings.accountNumberRmb"))}
              </div>
              <div>
                <FieldLabel htmlFor="company-logo">
                  {t("settings.companyLogo")}
                </FieldLabel>
                {draft.logoUrl && (
                  <img
                    src={draft.logoUrl}
                    alt={t("settings.companyLogo")}
                    className="mb-2 max-h-16 max-w-48 object-contain"
                  />
                )}
                <Input
                  id="company-logo"
                  type="file"
                  accept={IMAGE_ACCEPT}
                  disabled={uploading || save.isPending}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    setUploading(true);
                    setError("");
                    try {
                      const r = await uploadImage(file);
                      setDraft((d) => ({
                        ...d,
                        logoUrl: r.url,
                        logoKey: r.key,
                      }));
                      setReviewed(false);
                    } catch (err) {
                      setError(getLocalizedError(err, t, "settings.errorSave"));
                    } finally {
                      setUploading(false);
                    }
                  }}
                />
                {uploading && <p role="status">{t("common.saving")}</p>}
                {draft.logoUrl && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={uploading || save.isPending}
                    onClick={() => {
                      setDraft({ ...draft, logoUrl: "", logoKey: "" });
                      setReviewed(false);
                    }}
                  >
                    {t("common.remove")}
                  </Button>
                )}
              </div>
              <div>
                <FieldLabel htmlFor="company-active">
                  {t("common.status")}
                </FieldLabel>
                <Select
                  id="company-active"
                  value={draft.active ? "active" : "inactive"}
                  disabled={
                    save.isPending || !!(editing !== "new" && editing.isDefault)
                  }
                  onChange={(e) => {
                    setDraft({ ...draft, active: e.target.value === "active" });
                    setReviewed(false);
                  }}
                >
                  <option value="active">
                    {t("settingsWorkspace.active")}
                  </option>
                  <option value="inactive">
                    {t("settingsWorkspace.inactive")}
                  </option>
                </Select>
                {editing !== "new" && editing.isDefault && (
                  <p className="mt-1 text-xs text-ink-faint">
                    {t("settingsWorkspace.defaultProtection")}
                  </p>
                )}
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!!draft.isDefault}
                  disabled={
                    save.isPending ||
                    !draft.active ||
                    !!(editing !== "new" && editing.isDefault)
                  }
                  onChange={(e) => {
                    setDraft({ ...draft, isDefault: e.target.checked });
                    setReviewed(false);
                  }}
                />
                {t("settings.setAsDefaultCompany")}
              </label>
              <label className="flex items-start gap-2 rounded-sm border border-warn/30 bg-warn-tint p-3 text-xs">
                <input
                  type="checkbox"
                  checked={reviewed}
                  onChange={(e) => setReviewed(e.target.checked)}
                />
                {t("settingsWorkspace.reviewBank")}
              </label>
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <div className="sticky bottom-0 flex gap-2 border-t border-line bg-paper py-3">
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    !dirty ||
                    !reviewed ||
                    uploading ||
                    save.isPending ||
                    !draft.beneficiary.trim()
                  }
                >
                  {save.isPending ? t("common.saving") : t("common.save")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={uploading || save.isPending}
                  onClick={close}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
            {discard && (
              <div
                role="alertdialog"
                aria-label={t("settingsWorkspace.unsaved")}
                className="space-y-3 rounded-md border border-warn p-3"
              >
                <p>{t("settingsWorkspace.leaveHint")}</p>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setDiscard(false)}
                >
                  {t("settingsWorkspace.stay")}
                </Button>{" "}
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setEditing(null)}
                >
                  {t("settingsWorkspace.discard")}
                </Button>
              </div>
            )}
            {editing !== "new" && (
              <ConfigurationHistory resource="company" id={editing.id} />
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
